import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Chip,
  Stack,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Speed,
  Security,
  SupportAgent,
  TrendingUp,
  Public,
  ArrowForward,
  PlayCircle,
  CheckCircle,
  East,
  Router,
  CellTower,
  Storage,
  Wifi,
  Cloud,
  Analytics,
  NetworkCell,
  Shield,
  HeadsetMic,
  Bolt,
  Language,
  Business,
  Handshake,
  People,
} from '@mui/icons-material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  const features = [
    { icon: <Bolt />, title: 'Lightning Fast', desc: 'Up to 100Gbps connectivity', color: '#e60108', delay: 0.3 },
    { icon: <Shield />, title: 'Bank-Level Security', desc: 'Enterprise-grade protection', color: '#fefefe', delay: 0.4 },
    { icon: <HeadsetMic />, title: '24/7 Support', desc: 'Global support team', color: '#e60108', delay: 0.5 },
    { icon: <TrendingUp />, title: '99.999% Uptime', desc: 'Industry leading reliability', color: '#fefefe', delay: 0.6 },
  ];

  const statsData = [
    { value: '500,000+', label: 'KM of Fiber Network', color: '#e60108' },
    { value: '10,000+', label: 'Telecom Towers', color: '#191919' },
    { value: '150+', label: 'Countries Served', color: '#e60108' },
    { value: '100%', label: 'Uptime Guarantee', color: '#191919' },
  ];

  const services = [
    { 
      title: 'Fiber Network', 
      desc: '500,000+ km of high-speed fiber optic connectivity', 
      icon: <Router />, 
      color: '#e60108',
      image: '/images/Fiber Optic Network.png',
      stats: '100+ Tbps Capacity',
    },
    { 
      title: 'Telecom Towers', 
      desc: '10,000+ towers with 5G-ready infrastructure', 
      icon: <CellTower />, 
      color: '#191919',
      image: '/images/Telecom Tower Infrastructure.jpg',
      stats: '98% Coverage',
    },
    { 
      title: 'Data Centers', 
      desc: '50+ secure data centers worldwide', 
      icon: <Storage />, 
      color: '#e60108',
      image: '/images/DataCenters.jpg',
      stats: 'Tier III+ Certified',
    },
    { 
      title: 'Internet Solutions', 
      desc: 'High-speed internet for businesses of all sizes', 
      icon: <NetworkCell />, 
      color: '#a0a0a0',
      image: '/images/Internet Solutions.jpg',
      stats: 'Up to 100Gbps',
    },
  ];

  const partners = [
    { name: 'Worldwide Capital', icon: <Business />, industry: 'Finance', color: '#e60108' },
    { name: 'Worldwide Distribution', icon: <Handshake />, industry: 'Logistics', color: '#191919' },
    { name: 'Worldwide Chat', icon: <People />, industry: 'Communications', color: '#e60108' },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Background Image with Parallax Effect */}
        <motion.div
          style={{ 
            position: 'absolute',
            width: '100%',
            height: '110%',
            top: '-5%',
            left: 0,
            scale: scale,
          }}
        >
          <Box
            component="img"
            src="/images/telecommmunication.jpg"
            alt="Telecommunication Network"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </motion.div>

        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 50%, rgba(230,1,8,0.2) 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Global Leader in Communication"
              icon={<Language sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                backdropFilter: 'blur(10px)',
                fontWeight: 700,
                letterSpacing: '0.5px',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '5rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.4)',
              }}
            >
              Connecting the World,
              <br />
              <Box 
                component="span" 
                sx={{ 
                  color: '#e60108',
                  textShadow: '0 0 30px rgba(230,1,8,0.5)',
                  display: 'inline-block',
                }}
              >
                Bridging Continents
              </Box>
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                color: '#fefefe',
                mb: 4,
                maxWidth: 650,
                fontSize: '1.25rem',
                lineHeight: 1.6,
                textShadow: '0 1px 10px rgba(0,0,0,0.3)',
                opacity: 0.95,
              }}
            >
              Global communication solutions that power businesses across 150+ countries with cutting-edge infrastructure and 5G-ready technology.
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/contact')}
                  endIcon={<East />}
                  sx={{
                    bgcolor: '#e60108',
                    color: '#fefefe',
                    px: 5,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 3,
                    '&:hover': {
                      bgcolor: '#b80006',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(230,1,8,0.4)',
                    },
                  }}
                >
                  Get Started
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/services')}
                  startIcon={<PlayCircle />}
                  sx={{
                    color: '#fefefe',
                    borderColor: '#fefefe',
                    px: 5,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderRadius: 3,
                    '&:hover': {
                      borderColor: '#e60108',
                      bgcolor: 'rgba(230,1,8,0.15)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Explore Services
                </Button>
              </motion.div>
            </Stack>

            {/* Feature Pills - Centered */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              justifyContent="center"
              sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(254,254,254,0.2)' }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: feature.delay }}
                >
                  <Stack 
                    direction="row" 
                    spacing={1.5} 
                    alignItems="center"
                    sx={{
                      p: 1,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                        transform: 'translateX(5px)',
                      },
                    }}
                  >
                    <Box sx={{ color: feature.color, bgcolor: 'rgba(254,254,254,0.15)', p: 1, borderRadius: 2 }}>
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="#fefefe">
                        {feature.title}
                      </Typography>
                      <Typography variant="caption" color="rgba(254,254,254,0.8)">
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Stack>
                </motion.div>
              ))}
            </Stack>
          </motion.div>
        </Container>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            position: 'absolute',
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          <IconButton
            sx={{ 
              color: '#fefefe',
              bgcolor: 'rgba(230,1,8,0.3)',
              backdropFilter: 'blur(10px)',
              '&:hover': { 
                bgcolor: '#e60108',
                transform: 'scale(1.1)',
              },
            }}
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <Box component="span" sx={{ fontSize: '1.5rem' }}>↓</Box>
          </IconButton>
        </motion.div>
      </Box>

      {/* Stats Section - Simplified & Centered */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} justifyContent="center">
            {statsData.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ textAlign: 'center' }}
                >
                  <Typography 
                    variant="h2" 
                    fontWeight="800" 
                    sx={{ 
                      color: stat.color,
                      fontSize: { xs: '1.8rem', md: '2.5rem' },
                      mb: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#a0a0a0' }}>
                    {stat.label}
                  </Typography>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Overview */}
      <Box sx={{ py: 12, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Services"
              sx={{
                mb: 2,
                bgcolor: '#e6010810',
                color: '#e60108',
                fontWeight: 600,
                mx: 'auto',
                display: 'table',
              }}
            />
            <Typography
              variant="h2"
              sx={{ mb: 2, fontWeight: 800, color: '#191919' }}
            >
              Our Core Services
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}
            >
              Comprehensive communication solutions tailored to your needs
            </Typography>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      borderRadius: 4,
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      },
                    }}
                    onClick={() => navigate('/services')}
                  >
                    <Box
                      sx={{
                        height: 200,
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="img"
                        src={service.image}
                        alt={service.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, ${service.color}60 0%, transparent 100%)`,
                        }}
                      />
                      <Chip
                        label={service.stats}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: 16,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: '#fefefe',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: service.color + '15',
                          color: service.color,
                          mb: 2,
                          mx: 'auto',
                        }}
                      >
                        {service.icon}
                      </Avatar>
                      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#191919' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', lineHeight: 1.6 }}>
                        {service.desc}
                      </Typography>
                      <Button
                        endIcon={<ArrowForward />}
                        sx={{ 
                          mt: 2, 
                          color: service.color,
                          p: 0,
                          '&:hover': { transform: 'translateX(5px)', backgroundColor: 'transparent' },
                        }}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* View All Services Button - Centered */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/services')}
                endIcon={<ArrowForward />}
                sx={{
                  borderColor: '#e60108',
                  color: '#e60108',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    borderColor: '#b80006',
                    bgcolor: '#e6010810',
                  },
                }}
              >
                View All Services
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box sx={{ py: 12, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip
                  label="Why Choose Us"
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Setting the Standard for <br />
                  <Box component="span" sx={{ color: '#e60108' }}>Global Communication</Box>
                </Typography>
                <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8 }}>
                  With over a decade of experience, we've built the most reliable and extensive 
                  communication network globally, serving thousands of businesses across every continent.
                </Typography>
                <Stack spacing={2} sx={{ mt: 4 }}>
                  {[
                    { text: 'Global reach across 150+ countries', icon: <Language sx={{ fontSize: 20 }} /> },
                    { text: '99.999% uptime guaranteed', icon: <CheckCircle sx={{ fontSize: 20 }} /> },
                    { text: 'ISO 27001 certified security', icon: <Shield sx={{ fontSize: 20 }} /> },
                    { text: '24/7 multilingual support', icon: <HeadsetMic sx={{ fontSize: 20 }} /> },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Stack 
                        direction="row" 
                        spacing={1.5} 
                        alignItems="center"
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: '#e6010808',
                            transform: 'translateX(8px)',
                          },
                        }}
                      >
                        <Box sx={{ color: '#e60108' }}>{item.icon}</Box>
                        <Typography variant="body1" sx={{ color: '#191919', fontWeight: 500 }}>{item.text}</Typography>
                      </Stack>
                    </motion.div>
                  ))}
                </Stack>
                
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/about')}
                    sx={{
                      bgcolor: '#e60108',
                      px: 4,
                      py: 1.5,
                      mt: 4,
                      borderRadius: 3,
                      '&:hover': {
                        bgcolor: '#b80006',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Learn More About Us
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="/images/DataCenters.jpg"
                  alt="Data Center"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Trusted Partners Section - Centered */}
      <Box sx={{ py: 12, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Trusted Partners"
              sx={{
                mb: 2,
                bgcolor: '#e6010810',
                color: '#e60108',
                fontWeight: 600,
                mx: 'auto',
                display: 'table',
              }}
            />
            <Typography
              variant="h2"
              sx={{ mb: 2, fontWeight: 800, color: '#191919' }}
            >
              Trusted Partners
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}
            >
              Collaborating with industry leaders worldwide
            </Typography>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {partners.map((partner, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: partner.color + '15',
                        color: partner.color,
                        margin: '0 auto 16px',
                      }}
                    >
                      {partner.icon}
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                      {partner.name}
                    </Typography>
                    <Chip
                      label={partner.industry}
                      size="small"
                      sx={{
                        bgcolor: partner.color + '10',
                        color: partner.color,
                      }}
                    />
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Premium CTA Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #e60108 0%, #191919 100%)',
          color: '#fefefe',
          py: 10,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 800 }}>
              Ready to Transform Your Business?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
              Join 10,000+ businesses using our worldwide communication network
            </Typography>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/contact')}
                endIcon={<East />}
                sx={{
                  bgcolor: '#fefefe',
                  color: '#e60108',
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  '&:hover': {
                    bgcolor: '#fefefe',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                  },
                }}
              >
                Get Started Today
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;